import { connect } from 'react-redux';

import SinglePropertyDashboard from './component';
import { requestMarkAcknowledged } from '@rental-manager/actions/requests';
import {
  DEFAULT_ERROR,
  FAST_PAYMENTS_BANNER,
  HOSTED_ONBOARDING_COMPLETED,
  HOSTED_ONBOARDING_REQUIRED,
  IN_BETWEEN_TERMS,
  INVITE_NEW_RENTER,
  RENTAL_PROTECTION_COVERAGE_REQUEST_NOT_SIGNED,
  RENTAL_PROTECTION_FIRST_RENT_PAYMENT_FAILED,
  RENTAL_PROTECTION_LATEST_RENT_PAYMENT_FAILED,
  RENTAL_PROTECTION_MISSED_RENT_PAYMENT_BANNER,
  RENTAL_PROTECTION_POLICY_PENDING,
} from '@rental-manager/constants/NotificationConstants';
import type { ErrorInterface } from '@common/types';
import { BANNERS } from '@rental-manager/constants/BannerConstants';
import {
  closeNotification,
  showPageNotification,
  showStackedPageNotification,
} from '@common/actions/NotificationActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import HTTP_STATUS from '@common/constants/HttpConstants';
import FeaturePermissionTypes from '@rental-manager/constants/FeaturePermissionTypes';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (state, { propertyId }) => {
  const {
    landlordStatus,
    dismissibleActions,
    currentTerm,
    nextTerm = {},
    allTransactions,
    featurePermissions,
    userConfig,
    isLatestRentPaymentFailed,
    shouldDisplayRentalProtectionFirstRentFailedBanner,
    shouldDisplayRentalProtectionMissedRentBanner,
    leasesForProperty,
    selectedLease,
    banners,
    listingAlias,
  } = state.payments.landlord.property;
  const shouldInviteNewRenter = currentTerm.termEnded && nextTerm.termNotStarted;
  const { rentTermsKey, tenantEmail } = currentTerm;
  const rentTermsExtendPermissionCurrentTerm =
    featurePermissions?.[rentTermsKey]?.[FeaturePermissionTypes.RENT_TERMS_EXTEND] || {};
  const nextTermRentTermsKey = nextTerm?.rentTermsKey || '';
  const rentTermsExtendPermissionNextTerm =
    featurePermissions?.[nextTermRentTermsKey]?.[FeaturePermissionTypes.RENT_TERMS_EXTEND] || {};
  const { isRentGuaranteeEnabled } = userConfig;
  const rentGuarantee = featurePermissions?.[rentTermsKey]?.[FeaturePermissionTypes.RENT_GUARANTEE].exhausted;
  const isRentalProtectionVariation = isRentGuaranteeEnabled && rentGuarantee;
  const { paymentsEncodedLeaseId } = selectedLease || {};

  return {
    banners,
    rentTermsKey,
    paymentsEncodedLeaseId,
    propertyId,
    landlordStatus,
    fromEmail: state.router.location.search ? state.router.location.search.indexOf('email') !== -1 : false,
    dismissibleActions,
    allTransactions,
    shouldInviteNewRenter,
    tenantFirstName: currentTerm.tenantFirstName,
    rentTermsExtendPermissionCurrentTerm,
    rentTermsExtendPermissionNextTerm,
    currentTerm,
    nextTerm,
    isRentGuaranteeEnabled,
    isRentalProtectionVariation,
    isLatestRentPaymentFailed,
    shouldDisplayRentalProtectionFirstRentFailedBanner,
    shouldDisplayRentalProtectionMissedRentBanner,
    leasesForProperty,
    tenantEmail,
    listingAlias,
    shouldShowHostedOnboardingInformationReceivedNotification: state.router.location.search
      ? state.router.location.search.indexOf('showHostedOnboardingInformationReceivedBanner') !== -1
      : false,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId }) => ({
  showFastPaymentsBannerNotification: () => dispatch(showPageNotification(FAST_PAYMENTS_BANNER)),
  showBanners: (banners, paymentsEncodedLeaseId, rentTermsKey) => {
    banners.map((banner) => {
      const bannerName = BANNERS[banner.type];
      const bannerArgs = {
        baseUrl,
        propertyId,
        paymentsEncodedLeaseId,
        rentTermsKey,
      };
      return dispatch(showStackedPageNotification(bannerName, bannerArgs));
    });
  },
  showHostedOnboardingRequiredNotification: () =>
    dispatch(showStackedPageNotification(HOSTED_ONBOARDING_REQUIRED, { baseUrl })),
  showHostedOnboardingInformationReceivedNotification: () =>
    dispatch(showStackedPageNotification(HOSTED_ONBOARDING_COMPLETED, { baseUrl })),
  showRentalProtectionMissedRentNotification: () =>
    dispatch(showStackedPageNotification(RENTAL_PROTECTION_MISSED_RENT_PAYMENT_BANNER, { baseUrl })),
  showRentalProtectionDocumentNotSignedNotification: (listingAlias, policyId) =>
    dispatch(
      showStackedPageNotification(RENTAL_PROTECTION_COVERAGE_REQUEST_NOT_SIGNED, {
        baseUrl,
        propertyId,
        listingAlias,
        policyId,
      }),
    ),
  showRentalProtectionPolicyPendingNotification: () =>
    dispatch(showStackedPageNotification(RENTAL_PROTECTION_POLICY_PENDING)),
  showRentalProtectionFirstRentPaymentFailedNotification: (tenantEmail) =>
    dispatch(showStackedPageNotification(RENTAL_PROTECTION_FIRST_RENT_PAYMENT_FAILED, { tenantEmail })),
  showRentalProtectionLatestRentPaymentFailedNotification: (tenantEmail) =>
    dispatch(showStackedPageNotification(RENTAL_PROTECTION_LATEST_RENT_PAYMENT_FAILED, { tenantEmail })),
  showInviteNewRenterNotification: (
    rentTerms,
    rentTermsExtendPermissionCurrentTerm,
    leasesForProperty,
    isRentGuaranteeEnabled,
    listingAlias,
  ) =>
    dispatch(
      showStackedPageNotification(INVITE_NEW_RENTER, {
        baseUrl,
        propertyId,
        rentTerms,
        rentTermsExtendPermissionCurrentTerm,
        leasesForProperty,
        isRentGuaranteeEnabled,
        listingAlias,
      }),
    ),
  showInBetweenTermsNotification: ({ previousTermEnded, nextTermStarts }) =>
    dispatch(showStackedPageNotification(IN_BETWEEN_TERMS, { baseUrl, propertyId, previousTermEnded, nextTermStarts })),
  acknowledgeRolloutModal: async (actionId) => {
    try {
      await dispatch(closeNotification());
      const markAcknowledgedResponse = await dispatch(requestMarkAcknowledged({ propertyId, actionId }));
      await checkRequestActionSuccess(markAcknowledgedResponse);

      return { error: false };
    } catch (err) {
      logError({ error: err });
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }

      dispatch(showStackedPageNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SinglePropertyDashboard);
